import { isEmpty } from 'lodash';
import { makeApiPatchRequest, makeApiPostRequest, makeApiGetRequest } from './apiActions';
import { setNextResumeResultItem } from '../resume/resumeResultActions';
import DNOServiceClient from '../clients/DNOServiceClient';
import { RESULT_RESPONSE_OUTCOME, ACCURACY, LN } from '../../constants/measure';
import {
    incrementIncorrectQuestionsCounter, resetIncorrectQuestionsCounter, setResult,
    clearResult,
} from './resultActions';
import selectors from '../../rootSelectors';
import { QUESTIONS } from '../../constants/assessmentItemType';

export const saveResultResponse = (resultId, formId, measure, measureType, responseData,
    isComplete = false) => (dispatch) => {
    const patchData = {
        operation: (isComplete
            ? DNOServiceClient.RESULT_OPERATION.MARK_COMPLETE
            : DNOServiceClient.RESULT_OPERATION.ADD_RESPONSE),
        measure,
        measure_type: measureType,
        data: responseData,
    };

    const endpoint = DNOServiceClient.constructResultEndpoint(resultId);
    return dispatch(makeApiPatchRequest(endpoint, patchData))
        .then((response) => {
            if (response.outcome === RESULT_RESPONSE_OUTCOME.NOT_CORRECT
                    || response.outcome === RESULT_RESPONSE_OUTCOME.SKIPPED) {
                dispatch(incrementIncorrectQuestionsCounter());
            } else if (response.outcome === RESULT_RESPONSE_OUTCOME.CORRECT) {
                dispatch(resetIncorrectQuestionsCounter());
            }

            // Reset resume result next item because it's no longer accurate after
            // we make change to the result.
            dispatch(setNextResumeResultItem(resultId));

            // result status should be updated if completeness has changed
            if (isComplete) {
                dispatch(setResult({
                    result_id: resultId,
                    is_complete: isComplete,
                    form_id: formId,
                    measure,
                    measure_type: measureType,
                }));
            }

            return isComplete;
        });
};

export const discontinueResult = (resultId) => (dispatch) => {
    const patchData = {
        operation: DNOServiceClient.RESULT_OPERATION.MARK_DISCONTINUED,
    };
    const endpoint = DNOServiceClient.constructResultEndpoint(resultId);
    return dispatch(makeApiPatchRequest(endpoint, patchData));
};

export const createResult = (measure, measureType, formId) => (dispatch, getStore) => {
    const state = getStore();
    const instId = selectors.getInstId(state, measure);
    const postBody = {
        assessment: DNOServiceClient.NAME,
        assessment_mode: 'online',
        form_id: formId,
        grade_level: selectors.getGrade(state),
        inst_uid: instId,
        period: selectors.getToy(state),
        student_uid: selectors.getUserId(state),
        school_year: selectors.getSchoolYear(state),
        student_temporal_id: selectors.getStudentTemporalId(state, instId) || undefined,  // unset if not available
        measure,
        measure_type: measureType,
    };
    const endpoint = DNOServiceClient.constructResultListEndpoint();
    return dispatch(makeApiPostRequest(endpoint, postBody))
        .then((resultJson) => {
            dispatch(setResult(resultJson));
            return resultJson.result_id;
        });
};

export const getStudentResults = (measure) => (dispatch, getStore) => {
    const state = getStore();
    const studentId = selectors.getUserId(state);
    const grade = selectors.getGrade(state);
    const period = selectors.getToy(state);
    const schoolYear = selectors.getSchoolYear(state);
    const config = {
        ...(measure !== undefined ? { measure } : {}),
        studentId,
        grade,
        period,
        schoolYear,
        withItemLevel: true,
    };
    const endpoint = DNOServiceClient.constructResultListEndpoint(config);
    return dispatch(makeApiGetRequest(endpoint))
        .then((data) => {
            const resultsData = isEmpty(data) ? [] : data;
            resultsData.forEach((resultData) => {
                dispatch(setResult(resultData));
            });
        });
};

export const saveMeasureResponse = (measure, measureType, formId, response, isComplete = false) => (dispatch, getStore) => {
    const state = getStore();
    const result = selectors.findResultFor(state,
        {
            measure, measureType, formId, isComplete: false,
        });
    let createResultPromise;
    if (result !== undefined) {
        createResultPromise = Promise.resolve(result.id);
    } else {
        createResultPromise = dispatch(createResult(measure, measureType, formId));
    }
    return createResultPromise.then((postedResultId) => dispatch(saveResultResponse(
        postedResultId, formId, measure, measureType, response, isComplete,
    )));
};

export const saveLNMeasureResponse = (response, questionIndex, isLastQuestion) =>
    // Data format for PATCH result
    // {
    //     "operation": "add_response_with_complete",
    //     "measure": "LN",
    //     "measure_type": "accuracy",
    //     "data": {
    //          "item_id": "item1",
    //          "item_idx": 1,
    //          "form_id": "ln_accuracy_kg_boy_v0",
    //          "response_id": "q"
    //     }
    // }
    (dispatch, getStore) => {
        const state = getStore();
        const itemIdx = questionIndex - 1;
        const { itemId } = selectors.getLNContent(state, QUESTIONS, itemIdx);
        const formId = selectors.getLNFormId(state);
        const responseData = {
            item_id: itemId,
            item_idx: itemIdx,
            form_id: formId,
            response_id: response,
        };
        return dispatch(saveMeasureResponse(
            LN.NAME,
            ACCURACY,
            formId,
            responseData,
            isLastQuestion,
        ));
    };
export const discontinueMeasure = (measure, measureType, formId) => (dispatch, getStore) => {
    const state = getStore();
    const result = selectors.findResultFor(state, { measure, measureType, formId });

    return dispatch(discontinueResult(result.id));
};

export const deleteResult = (measure, measureType, formId) => (dispatch, getStore) => {
    const state = getStore();
    let deleteResultPromise;
    const result = selectors.findResultFor(state, { measure, measureType, formId });
    if (result) {
        deleteResultPromise = dispatch(DNOServiceClient.deleteStudentResult(result.id))
            .then(() => {
                dispatch(clearResult(result.id));
            });
    } else {
        deleteResultPromise = Promise.resolve();
    }

    return deleteResultPromise;
};
